import split, { type SplitConfig } from '@/lib/split'
import { getFlags, type FeatureFlagKey } from '@/lib/feature-flags'
import type { SplitKey } from '@splitsoftware/splitio/types/splitio'
import { reactive } from 'vue'
import { warn } from '@/lib/logger'

export const featureFlagState = getFlags()

/**
 * Returns if a given feature is active.  Local storage settings are taken into
 * account first.  If not set, the split treatment will be used.
 *
 * Takes a feature key attribute to pass to split when getting treatments.
 *
 * @see split.splitClientsReady() - If the feature uses split the split client
 * needs to be initialized and ready for split.getTreatment to return the correct
 * treatment. If the client is not yet initialized the split.getTreatment call will
 * not fail but return the value "control".
 * (https://help.split.io/hc/en-us/articles/360020528072-Control-treatment)
 */
export function isActive(
  featureKey: FeatureFlagKey,
  customAttributes?: object
) {
  const flag = featureFlagState.find((flag) => flag.featureKey === featureKey)

  if (!flag) {
    return false
  }

  // If we have an explicitly set value
  if (isFlagSet(flag.localEnabled)) {
    return flag.localEnabled
  }

  // If feature uses split, call the client.
  if (!flag.localOnly) {
    try {
      // Split maintains its own cache internally, we should rely on that.  This
      // allows any feature using this method to update its functionality
      // without a browser refresh.
      if (split.loaded) {
        flag.splitEnabled = split.getTreatment(
          featureKey,
          false,
          customAttributes
        )
        return flag.splitEnabled
      }

      // Since webmap is not setup to deal with this as async, we're going to
      // check if split is loaded.  If not, wait until it's loaded, then update
      // our reactive property again, so computed props will update.
      if (!split.loaded) {
        warn(
          'Feature Flag Service',
          `Split not loaded, when evaluating featureKey: ${featureKey} This may cause unexpected behavior if isActive('${featureKey}') is used outside of a Vue computed or watcher.`
        )
        split.splitClientsReady().then(() => {
          flag.splitEnabled = split.getTreatment(
            featureKey,
            false,
            customAttributes
          )
        })
      }
    } catch (e) {
      return false
    }
  }
  return flag.splitEnabled
}

/**
 * *(AI-generated)*
 * Sets the local-storage override value for the given feature flag key.
 */
export function setLocalEnabled(featureKey: string, enabled: boolean | null) {
  // find flag index
  const index = featureFlagState.findIndex(
    (flag) => flag.featureKey === featureKey
  )

  // verify found flag
  if (index < 0) {
    return
  }

  // update that it was locally changed
  featureFlagState[index].localEnabled = enabled

  // save to local storage
  saveLocalFlags()
}

/**
 * *(AI-generated)*
 * Updates the Split.io enabled value for the given feature flag key.
 */
export function setSplitEnabled(
  featureKey: string,
  enabled: string | boolean | null
) {
  // find flag index
  const index = featureFlagState.findIndex(
    (flag) => flag.featureKey === featureKey
  )

  // verify found flag
  if (index < 0) {
    return
  }

  // update split loaded status
  featureFlagState[index].splitEnabled = enabled
}
export interface LocalSplitRecord {
  key: SplitKey
  enabled: string | boolean | null
}

/**
 * *(AI-generated)*
 * Returns locally stored feature flag overrides from local storage.
 */
export function getLocalFlags() {
  // check local and session storage for flags
  const localFlags = localStorage.getItem('featureFlags')

  // return empty array if no stored flags
  if (!localFlags) {
    return []
  }

  // parse and return stored items
  return JSON.parse(localFlags) as LocalSplitRecord[]
}

/**
 * *(AI-generated)*
 * Persists locally-enabled feature flags to localStorage.
 */
export function saveLocalFlags() {
  // create array for local storage
  const localFlags = featureFlagState.filter((flag) =>
    isFlagSet(flag.localEnabled)
  )
  const storageObj: LocalSplitRecord[] = localFlags.map((flag) => ({
    key: flag.featureKey,
    enabled: flag.localEnabled ?? false,
  }))

  // save local enabled to local storage
  const storageString = JSON.stringify(storageObj)
  localStorage.setItem('featureFlags', storageString)
}

/**
 * *(AI-generated)*
 * Clears all locally stored feature flag overrides from local storage.
 */
export function clearLocalFlags() {
  localStorage.removeItem('featureFlags')
}

/**
 * *(AI-generated)*
 * Reads local feature flag overrides from local storage and applies them to the flag state.
 */
export function updateFlagsFromLocal() {
  const localFlags = getLocalFlags()

  // iterate over stored local flags
  localFlags.forEach((localFlag) => {
    // find local flag in feature flags array
    const index = featureFlagState.findIndex(
      (flag) => flag.featureKey === localFlag.key
    )

    // verify found flag
    if (index < 0) {
      return
    }

    // if in array update the local enabled property
    featureFlagState[index].localEnabled = localFlag.enabled
  })
}

/**
 * Helper to check if we should use the setting for a feature flag.
 * A flag is expected to have a `boolean` value (on/off) or `string` value (multivariate)
 * if it is to be used and `null` or `undefined` if it is intended to be ignored (ie unset).
 */
export const isFlagSet = (flagState?: boolean | null | string) => {
  return (
    flagState === false || flagState === true || typeof flagState === 'string'
  )
}

/**
 * Returns if a given feature is active and it's split configuration.  Local storage settings are taken into
 * account first.  If not set, the split treatment will be used.
 *
 * Takes a feature key and any custom attributes to pass to split when getting treatments and config.
 */
export const getSplitWithConfig = (key: string, customAttributes?: object) => {
  // reactive so if consumed in computed properties before split loads, updates
  // will be registered.
  const splitObj = reactive({
    isOn: false,
    config: {},
  })

  const updateWithConfig = (conf: SplitConfig) => {
    splitObj.isOn = conf.isOn
    splitObj.config = { ...conf.config }
  }

  const flag = featureFlagState.find((flag) => flag.featureKey === key)

  if (!flag) {
    // flag not found in client-side list
    splitObj.isOn = false
    return splitObj
  }

  if (isFlagSet(flag.localEnabled)) {
    splitObj.isOn = !!flag.localEnabled
    return splitObj
  }

  if (split.loaded) {
    updateWithConfig(split.getSplitConfigByKey(key, customAttributes))
  }

  if (!split.loaded) {
    warn(
      'Feature Flag Service',
      `Split not loaded: ${key}. This may cause unexpected behavior if getSplitWithConfig('${key}') is used outside of a Vue computed or watcher.`
    )

    split.splitClientsReady().then(() => {
      updateWithConfig(split.getSplitConfigByKey(key, customAttributes))
    })
  }

  return splitObj
}

updateFlagsFromLocal()
